/**
 * DailyCollectionSummary — Phase 12.6
 *
 * Six-tile widget at the top of the Manage Visit page. Shows the
 * day's collection split by payment mode (Cash / UPI / Card),
 * the day's total refunded, the headline Net Collection, and the
 * Diagnostic waiver given memo.
 *
 *   ┌─────────┬─────────┬─────────┬──────────┬──────────────────┬────────────────────────┐
 *   │ Cash    │  UPI    │  Card   │  Refunds │  Net Collection  │  Diagnostic waiver given│
 *   │ ₹4,200  │ ₹1,800  │  ₹500   │ −₹200    │     ₹6,300       │         ₹300            │
 *   └─────────┴─────────┴─────────┴──────────┴──────────────────┴────────────────────────┘
 *
 * Reads from GET /visits/daily-summary — the same helper that drives
 * the Busy export's summary block, so the tiles and the spreadsheet
 * can never drift. Re-fetches on:
 *   - mount + date / doctorId prop changes
 *   - the realtime VISIT_BUNDLED_PAYMENT_COLLECTED event (so a payment
 *     collected in another tab updates the tiles without a page
 *     reload)
 *
 * `Diagnostic waiver given` (DIAG-DISCOUNT / BE-7, FE-4) is the
 * `lab_waiver_rupees` field `compute_collection_summary` now returns —
 * ALWAYS present (₹0 when nothing was waived), read verbatim through
 * `selectLabWaiverRupees` (never re-derived from the other tiles). It is
 * a memo — already netted out of `net_collected_rupees` server-side, so
 * this tile explains WHY Net Collection is lower, it does not change it.
 *
 * Permission gate (`VIEW_DAILY_COLLECTION_SUMMARY`) is enforced by
 * ManageVisitPage at the call site — DOCTOR / NURSE never see this
 * widget.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { getDailyCollectionSummary } from '../../services/api/receiptsApi';
import eventBus from '../../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../../services/realtime/RealtimeSyncService';
import { DIAG_WAIVER_COPY } from '../../constants/diagWaiverCopy';


/**
 * Single source for the "Diagnostic waiver given" figure — reads
 * `lab_waiver_rupees` off the daily-summary response verbatim (server
 * already defaults it to 0.0 and sums it above the net<=0 guard, BE-7).
 * Never re-derived from the other tiles on this widget.
 */
export function selectLabWaiverRupees(data) {
  return Number(data?.lab_waiver_rupees ?? 0);
}

function fmtRupees(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return '₹0';
  // Indian grouping: 1,23,456.78 — Intl handles it via en-IN locale.
  const abs = Math.abs(num);
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: abs % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(abs);
  return num < 0 ? `−₹${formatted}` : `₹${formatted}`;
}


function Tile({
  label, value, isDark, tone = 'neutral', headline = false, loading = false, testId,
}) {
  // Tone palette — solid day-mode backgrounds + visible borders
  // per the project's contrast rules.
  const palette = (() => {
    if (headline) {
      return isDark
        ? { bg: '#134e4a', border: 'rgba(94,234,212,0.45)', label: '#5eead4', value: '#ffffff' }
        : { bg: '#ccfbf1', border: '#14b8a6',                label: '#0f766e', value: '#0f172a' };
    }
    if (tone === 'refund') {
      return isDark
        ? { bg: '#3b0a0a', border: 'rgba(239,68,68,0.5)',   label: '#fca5a5', value: '#fecaca' }
        : { bg: '#fef2f2', border: '#fca5a5',                label: '#b91c1c', value: '#7f1d1d' };
    }
    return isDark
      ? { bg: '#0f172a',   border: 'rgba(255,255,255,0.12)', label: '#94a3b8', value: '#e2e8f0' }
      : { bg: '#ffffff',   border: '#cbd5e1',                label: '#475569', value: '#0f172a' };
  })();

  return (
    <div
      data-testid={testId}
      style={{
        flex: headline ? '1.4 1 0' : '1 1 0',
        minWidth: '120px',
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: '0.625rem',
        padding: headline ? '0.75rem 1rem' : '0.625rem 0.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.2rem',
      }}
    >
      <div style={{
        fontSize: '0.7rem', fontWeight: 600,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: palette.label,
      }}>
        {label}
      </div>
      {loading ? (
        <div
          aria-label="Loading"
          style={{
            height: headline ? '1.5rem' : '1.25rem',
            width: headline ? '75%' : '60%',
            borderRadius: '0.25rem',
            background: isDark
              ? 'linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.14) 50%, rgba(255,255,255,0.06) 100%)'
              : 'linear-gradient(90deg, rgba(15,23,42,0.06) 0%, rgba(15,23,42,0.14) 50%, rgba(15,23,42,0.06) 100%)',
            backgroundSize: '200% 100%',
            animation: 'dcs-shimmer 1.2s ease-in-out infinite',
          }}
        />
      ) : (
        <div style={{
          fontSize: headline ? '1.25rem' : '1.05rem',
          fontWeight: headline ? 700 : 600,
          color: palette.value,
          lineHeight: 1.2,
        }}>
          {value}
        </div>
      )}
    </div>
  );
}


// Inject the shimmer keyframes once per app load. CRA / CSS-in-JS-less
// setup → easiest is a one-shot <style> tag we mount with the module.
if (typeof document !== 'undefined' && !document.getElementById('dcs-shimmer-kf')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'dcs-shimmer-kf';
  styleEl.textContent = (
    '@keyframes dcs-shimmer {'
    + ' 0% { background-position: 200% 0; }'
    + ' 100% { background-position: -200% 0; }'
    + '}'
  );
  document.head.appendChild(styleEl);
}


export default function DailyCollectionSummary({
  date,
  doctorId = null,
  // Provider scope (Manage Visits OPD / NURSE / LAB filter). null = org-wide.
  provider = null,
  isDark = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    setError(null);
    const res = await getDailyCollectionSummary(date, doctorId, provider);
    if (res.success) setData(res.data);
    else { setData(null); setError(res.error || 'Failed to load summary.'); }
    setLoading(false);
  }, [date, doctorId, provider]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Realtime — bundled-payment events tell us the totals have moved.
  useEffect(() => {
    const off = eventBus.on(
      REALTIME_EVENTS.VISIT_BUNDLED_PAYMENT_COLLECTED, fetchSummary,
    );
    return () => { off?.(); };
  }, [fetchSummary]);

  const byMode = data?.by_mode || {};
  const cash    = byMode.CASH || 0;
  const upi     = byMode.UPI  || 0;
  const card    = byMode.CARD || 0;
  const refunds = Number(data?.total_refunded_rupees || 0);  // ≤ 0
  const net     = Number(data?.net_collected_rupees  || 0);
  const labWaiver = selectLabWaiverRupees(data);

  const containerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.625rem',
    padding: '0.5rem 0 1rem',
  };

  if (error) {
    return (
      <div style={{
        padding: '0.5rem 0.75rem',
        background: isDark ? '#3b0a0a' : '#fef2f2',
        color: isDark ? '#fecaca' : '#991b1b',
        border: `1px solid ${isDark ? 'rgba(239,68,68,0.45)' : '#fca5a5'}`,
        borderRadius: '0.5rem',
        fontSize: '0.8rem',
        marginBottom: '0.75rem',
      }}>
        Daily summary: {error}
      </div>
    );
  }

  // First-load → show shimmer skeletons. Derived from data/error presence
  // (not the `loading` flag's timing) so the very first render — before
  // the fetch-triggering effect has even run — never slips through as
  // "not loading" and paints ₹0 as if it were real data. Background
  // refresh (data already exists, a refetch is in flight) keeps showing
  // the previous values — `aria-busy` on the container is the signal —
  // so the layout doesn't flicker on every realtime event.
  const isInitialLoad = !data && !error;

  return (
    <div
      style={containerStyle}
      aria-busy={loading ? 'true' : 'false'}
      aria-label="Daily collection summary"
    >
      <Tile label="Cash"           value={fmtRupees(cash)}    isDark={isDark} loading={isInitialLoad} />
      <Tile label="UPI"            value={fmtRupees(upi)}     isDark={isDark} loading={isInitialLoad} />
      <Tile label="Card"           value={fmtRupees(card)}    isDark={isDark} loading={isInitialLoad} />
      <Tile label="Refunds"        value={fmtRupees(refunds)} isDark={isDark} tone="refund" loading={isInitialLoad} />
      <Tile label="Net Collection" value={fmtRupees(net)}     isDark={isDark} headline       loading={isInitialLoad} />
      <Tile
        testId="tile-diagnostic-waiver-given"
        label={DIAG_WAIVER_COPY['labReport.diagnosticWaiverGiven.label']}
        value={fmtRupees(labWaiver)}
        isDark={isDark}
        loading={isInitialLoad}
      />
    </div>
  );
}
